import type { ReactNode } from 'react';
import { diasEntre, formatMoney, type Centavos } from '@abastece/shared';
import type { MotivoOferta } from '@abastece/core';
import { comparacionDeOferta, menosDe, nombreDeLleva, type Lleva, type SugerenciaOferta } from '../lib/ofertas.js';
import { etiquetaPresentacion } from '../i18n/dominio.js';
import { useT, type Clave } from '../i18n/index.js';
import { fechaCorta } from './CuentasDeCaja.js';
import { Ico } from './Iconos.js';
import { MargenDe } from './Margen.js';

/**
 * What an offer does to a price, drawn the same everywhere on «Ofertas»: a table of before and after
 * (the owner, 2026-11-03: «i need to see original price -> suggested price», then «clearly shows a
 * before and after column, also align everything more nicely … the cards outside of the form should
 * also show a clear before and after regarding the prices/margins»).
 */

/** «−30%», «3×2»: what an offer does, at a glance. */
export function Insignia({ children }: { children: ReactNode }) {
  return (
    <span
      className="tabular inline-flex shrink-0 items-center rounded-lg bg-teal-500/15 px-2 py-0.5 text-sm font-bold whitespace-nowrap text-teal-200 ring-1 ring-teal-400/25 ring-inset"
      data-oferta-insignia
    >
      {children}
    </span>
  );
}

/** A column's head: small capitals, to the right as its figures are. */
const CABEZA = 'text-right text-[11px] leading-4 font-semibold tracking-wider uppercase';

/**
 * The table: a column of what each row is, «Antes» and «En oferta» with an arrow between them, each
 * figure to the right of its column so that they line up from one row, and one presentation, to the
 * next. Each presentation is a group of rows (`GrupoAntesDespues`) of four cells (`FilaAntesDespues`).
 * The two columns of figures are at most 9rem and keep to the right, the names taking the rest: on a
 * laptop's wide sheet, columns sharing the width put each figure on offer a hand away from its
 * arrow. The names' column is a plain `1fr`: at `minmax(max-content, 1fr)` the lines across the
 * whole table (a presentation's name, the notes under it) gave it all their width, and at 320 px
 * the figures had none. On such a phone the gaps narrow, and a figure too long for its column
 * breaks after «C$».
 */
export function TablaAntesDespues({ children }: { children: ReactNode }) {
  const t = useT();
  return (
    <div
      className="grid grid-cols-[1fr_minmax(0,9rem)_auto_minmax(0,9rem)] items-center gap-x-3 gap-y-1.5 max-[359px]:gap-x-2"
      data-oferta-tabla
    >
      <span />
      <span className={`${CABEZA} text-slate-500`}>{t('ofertas.tabla.antes')}</span>
      <span />
      <span className={`${CABEZA} text-teal-300`}>{t('ofertas.tabla.enOferta')}</span>
      {children}
    </div>
  );
}

/**
 * A presentation's rows under a line of its own: its name (or a box to tick it, with it), what else
 * is said of it (`detalle`: what it costs), and the badge at the right, over the «En oferta» column.
 * Its cells stay the table's (`contents`), so the columns are the same for every presentation.
 */
export function GrupoAntesDespues({
  id,
  presentacion,
  detalle,
  insignia,
  primero,
  children,
}: {
  id: string;
  presentacion: ReactNode;
  detalle?: ReactNode;
  insignia?: ReactNode;
  primero: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="contents" data-oferta-cambio={id}>
      <div className={`col-span-full flex min-h-7 items-center justify-between gap-3 ${primero ? '' : 'mt-1.5 border-t border-white/5 pt-2.5'}`}>
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium text-slate-100" data-oferta-presentacion-de>
            {presentacion}
          </span>
          {detalle && <span className="tabular text-xs text-slate-500">{detalle}</span>}
        </div>
        {insignia}
      </div>
      {children}
    </div>
  );
}

/** A row of the table: what it is, its figure before, an arrow, its figure on offer. */
export function FilaAntesDespues({ etiqueta, antes, ahora, dato }: { etiqueta: string; antes: ReactNode; ahora: ReactNode; dato: string }) {
  return (
    <>
      <span className="text-xs whitespace-nowrap text-slate-400">{etiqueta}</span>
      <span className="tabular min-w-0 text-right" data-oferta-antes-de={dato}>
        {antes}
      </span>
      <span className="text-center text-slate-500">→</span>
      <span className="tabular min-w-0 text-right" data-oferta-ahora-de={dato}>
        {ahora}
      </span>
    </>
  );
}

/** «C$ 30.00» struck: the price the offer replaces. */
export const Tachado = ({ monto }: { monto: Centavos }) => (
  <s className="text-sm text-slate-400 decoration-slate-500" data-oferta-antes>
    {formatMoney(monto)}
  </s>
);

/** The list's price in the «Antes» column: struck when the offer replaces it, «—» with none. */
export function PrecioAntes({ monto, tachado }: { monto: Centavos | null; tachado: boolean }) {
  if (monto === null) return <span className="text-xs text-slate-600">—</span>;
  if (tachado) return <Tachado monto={monto} />;
  return <span className="text-sm text-slate-200">{formatMoney(monto)}</span>;
}

/** «C$ 21.00»: the offer's, larger and in teal. */
export const Destacado = ({ monto }: { monto: Centavos }) => (
  <span className="text-base font-semibold text-teal-300 max-[359px]:text-sm" data-oferta-ahora>
    {formatMoney(monto)}
  </span>
);

/** A presentation at an offer: its list's price, the offer, a lower price or so many for fewer, and what one costs. */
export interface CambioDeOferta {
  /** The presentation's id. */
  id: string;
  /** «Unidad», «Caja (12)». */
  presentacion: string;
  /** The list's price, without the offer; null when the list has none. */
  regular: Centavos | null;
  oferta: { precio: Centavos } | { lleva: Lleva };
  /** What one costs, for whoever sees costs; null otherwise, or when nobody knows. */
  costo: Centavos | null;
}

/**
 * An offer's presentations as a table (`TablaAntesDespues`), on «Ofertas»' cards: for each, «Precio»
 * (or «Precio de 3» at so many for fewer) before and on offer with how much off, «−30%», or the deal,
 * «3×2»; and for whoever sees costs, «Margen» before and on offer. A price no longer under the list's
 * (the list's lowered since) says the till charges the list's; so many for fewer with no price on the
 * list says there is none.
 */
export function PreciosDeOferta({ cambios, minimoBp }: { cambios: readonly CambioDeOferta[]; minimoBp: number }) {
  const t = useT();
  return (
    <TablaAntesDespues>
      {cambios.map((c, i) => {
        const v = comparacionDeOferta(c.regular, c.oferta, c.costo);
        const lleva = 'lleva' in c.oferta ? c.oferta.lleva : null;
        const insignia = lleva ? nombreDeLleva(lleva) : v.baja && v.antes !== null && v.ahora !== null ? menosDe(v.ahora, v.antes) : null;
        const nota =
          c.regular === null ? (lleva ? t('ofertas.sinPrecio') : null) : !v.baja && !lleva ? t('ofertas.cambio.sinEfecto', { monto: formatMoney(c.regular) }) : null;
        return (
          <GrupoAntesDespues key={c.id} id={c.id} presentacion={c.presentacion} insignia={insignia && <Insignia>{insignia}</Insignia>} primero={i === 0}>
            <FilaAntesDespues
              etiqueta={v.cuantas > 1 ? t('ofertas.tabla.precioDe', { n: v.cuantas }) : t('ofertas.tabla.precio')}
              dato="precio"
              antes={<PrecioAntes monto={v.antes} tachado={v.baja} />}
              ahora={v.ahora === null ? <span className="text-xs text-slate-600">—</span> : <Destacado monto={v.ahora} />}
            />
            {v.costo !== null && (
              <FilaAntesDespues
                etiqueta={t('ofertas.tabla.margen')}
                dato="margen"
                antes={<MargenDe precio={v.antes} costo={v.costo} minimoBp={minimoBp} />}
                ahora={<MargenDe precio={v.ahora} costo={v.costo} minimoBp={minimoBp} />}
              />
            )}
            {nota && <p className="col-span-full text-xs text-amber-300">{nota}</p>}
          </GrupoAntesDespues>
        );
      })}
    </TablaAntesDespues>
  );
}

/** A suggestion's presentations, as `PreciosDeOferta` draws them; what one costs only for whoever sees costs. */
export const cambiosDeSugerencia = (s: SugerenciaOferta, unidadBase: string, verCostos: boolean): CambioDeOferta[] =>
  s.precios.map((p) => ({
    id: p.unidadVentaId,
    presentacion: etiquetaPresentacion({ nombre: p.nombre, factor: p.factor }, unidadBase),
    regular: p.regular,
    oferta: { precio: p.oferta },
    costo: verCostos && p.costo > 0 ? p.costo : null,
  }));

/** Why an offer is, as a coloured word: what expires amber, what sits still sky, one by hand grey. */
const TINTE_MOTIVO: Record<MotivoOferta, string> = {
  vence: 'bg-amber-500/12 text-amber-300 ring-amber-500/20',
  lento: 'bg-sky-500/12 text-sky-300 ring-sky-500/20',
  otro: 'bg-slate-700/50 text-slate-300 ring-white/5',
};

export function Motivo({ motivo }: { motivo: MotivoOferta }) {
  const t = useT();
  return (
    <span className={`mr-1.5 inline-block rounded-md px-1.5 align-[1px] text-[11px] leading-5 font-semibold ring-1 ring-inset ${TINTE_MOTIVO[motivo]}`} data-oferta-motivo={motivo}>
      {t(`ofertas.motivo.${motivo}` as Clave)}
    </span>
  );
}

/**
 * What «Ofertas» suggests for a product, in a box of its own: «Sugerido», how much it takes off when
 * that is one figure (`quita`), for how many days and until when; on its card, the table of each
 * presentation before and with it (`conTabla`); on its sheet, where the table under it is the one to
 * change, not. What else the place says goes under it (`children`).
 */
export function Sugerido({
  sugerencia: s,
  unidadBase,
  verCostos,
  minimoBp,
  conTabla = true,
  quita,
  children,
}: {
  sugerencia: SugerenciaOferta;
  unidadBase: string;
  verCostos: boolean;
  minimoBp: number;
  conTabla?: boolean;
  quita?: string | null;
  children?: ReactNode;
}) {
  const t = useT();
  const dias = diasEntre(s.desde, s.hasta) + 1;
  return (
    <div className="rounded-xl bg-teal-500/[0.06] p-3 ring-1 ring-teal-500/15 ring-inset" data-ofertas-propuesta>
      <div className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 ${conTabla ? 'mb-2' : ''}`}>
        <p className="flex items-center gap-2 text-xs font-semibold tracking-wider text-teal-300 uppercase">
          {t('ofertas.sugerido')}
          {quita && <Insignia>{quita}</Insignia>}
        </p>
        <p className="flex items-center gap-1.5 text-xs text-slate-300" data-ofertas-duracion>
          <Ico icono="calendario" className="size-3.5 shrink-0 text-slate-400" />
          {dias === 1 ? t('ofertas.duracionHoy') : t('ofertas.duracion', { n: dias, hasta: fechaCorta(s.hasta) })}
        </p>
      </div>
      {conTabla && <PreciosDeOferta cambios={cambiosDeSugerencia(s, unidadBase, verCostos)} minimoBp={minimoBp} />}
      {children}
    </div>
  );
}
